// Margin follow-up of a project over the three terms of project cost control:
// the quote (what was sold), the execution budget (what is really planned to be
// spent) and the actuals.
//
//   study variance      budget − quote    did we sell well?
//   execution variance  actual − budget   is the project holding?
import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TypeChip } from "../helpers/costCategories"
import ProfitabilityHelpModal from "./ProfitabilityHelpModal"

const euros = cents => {
  if (cents == null) return "—"
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100)
}

const signedEuros = cents => {
  if (cents == null) return "—"
  if (cents === 0) return euros(0)
  return `${cents > 0 ? "+" : "−"} ${euros(Math.abs(cents))}`
}

const pct = value => (value == null || isNaN(value) ? "—" : `${Number(value).toFixed(1).replace(".", ",")} %`)
const coef = value => (value == null ? "—" : Number(value).toFixed(2).replace(".", ","))
const hours = value => (!value ? null : `${Number(value).toFixed(2).replace(".", ",")} ${I18n.t("projects.units.hour_short")}`)

// Spending more than planned is bad news, spending less is good — and the other
// way round on the profit.
const costTone   = cents => (cents > 0 ? "text-rose-600" : cents < 0 ? "text-emerald-600" : "text-slate-400")
const profitTone = cents => (cents > 0 ? "text-emerald-600" : cents < 0 ? "text-rose-600" : "text-slate-400")

const TH  = "px-3 py-2 font-semibold"
const TD  = "px-3 py-2.5 text-right tabular-nums whitespace-nowrap"
const GRP = "px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.12em] text-slate-400 border-b border-slate-200"

// Nothing spent is not a 100 % margin, it is nothing to measure yet.
const Pending = () => <span className="text-slate-300">—</span>

const MarginsSection = ({ margins }) => {
  const [helpOpen, setHelpOpen] = useState(false)

  if (!margins?.rows) return null

  const { rows, totals, has_forecast: hasForecast, has_budget: hasBudget } = margins

  return (
    <section id="section-margins" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">
            {I18n.t("projects.margins.title")}
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {I18n.t("projects.margins.subtitle")}
          </p>
        </div>
        <button
            type      = "button"
            onClick   = {() => setHelpOpen(true)}
            className = "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-[12px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            <FontAwesomeIcon icon="circle-question" className="text-slate-400" />
            {I18n.t("projects.profitability_help.open")}
          </button>
      </header>

      <ProfitabilityHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      {!hasForecast && (
        <div className="mb-3 flex items-start gap-2 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <FontAwesomeIcon icon="circle-info" className="mt-0.5 text-amber-500" />
          <span>{I18n.t("projects.margins.no_forecast")}</span>
        </div>
      )}

      {hasForecast && !hasBudget && (
        <div className="mb-3 flex items-start gap-2 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <FontAwesomeIcon icon="circle-info" className="mt-0.5 text-slate-400" />
          <span>{I18n.t("projects.margins.no_budget")}</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {/* Les trois termes, nommés une fois pour toutes. */}
              <tr className="bg-slate-50/70 text-left">
                <th className={GRP}></th>
                <th className={GRP} colSpan={2}>{I18n.t("projects.margins.group.quote")}</th>
                <th className={`${GRP} border-l border-slate-200`} colSpan={2}>{I18n.t("projects.margins.group.budget")}</th>
                <th className={`${GRP} border-l border-slate-200`} colSpan={2}>{I18n.t("projects.margins.group.actual")}</th>
                <th className={`${GRP} border-l border-slate-200`} colSpan={4}>{I18n.t("projects.margins.group.margin")}</th>
              </tr>
              <tr className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 text-left">
                <th className={TH}>{I18n.t("projects.margins.col.category")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.cost")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.sale")}</th>
                <th className={`${TH} text-right border-l border-slate-200`}>{I18n.t("projects.margins.col.cost")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.study_variance")}</th>
                <th className={`${TH} text-right border-l border-slate-200`}>{I18n.t("projects.margins.col.cost")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.execution_variance")}</th>
                <th className={`${TH} text-right border-l border-slate-200`}>{I18n.t("projects.margins.col.overhead_coef")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.budget_profit")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.actual_profit")}</th>
                <th className={`${TH} text-right`}>{I18n.t("projects.margins.col.profit_variance")}</th>
              </tr>
            </thead>

            <tbody className="text-[13px]">
              {rows.map(row => (
                <tr
                  key       = {row.category}
                  className = {`border-t border-slate-100 hover:bg-slate-50/60 ${row.empty ? "opacity-45" : ""}`}
                >
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <TypeChip type={row.category} />
                  </td>

                  {/* 1 — le devis */}
                  <td className={`${TD} text-slate-600`}>
                    {euros(row.quote_cost_cents)}
                    {hours(row.quote_hours) && (
                      <div className="text-[11px] text-slate-400">{hours(row.quote_hours)}</div>
                    )}
                  </td>
                  <td className={`${TD} text-slate-900`}>{euros(row.quote_sale_cents)}</td>

                  {/* 2 — le budget d'exécution */}
                  <td className={`${TD} text-slate-900 font-medium border-l border-slate-100`}>
                    {euros(row.budget_cost_cents)}
                    {row.budget_from_quote && !row.empty && (
                      <div className="text-[11px] text-slate-400 font-normal">
                        {I18n.t("projects.margins.from_quote")}
                      </div>
                    )}
                  </td>
                  <td className={`${TD} font-semibold ${costTone(row.study_variance_cents)}`}>
                    {signedEuros(row.study_variance_cents)}
                    {row.quote_cost_cents > 0 && row.study_variance_cents !== 0 && (
                      <div className="text-[11px] font-normal opacity-80">{pct(row.study_variance_pct)}</div>
                    )}
                  </td>

                  {/* 3 — le réel */}
                  <td className={`${TD} text-slate-900 font-medium border-l border-slate-100`}>
                    {row.has_actual ? euros(row.actual_cost_cents) : <Pending />}
                    {row.has_actual && (row.committed_cost_cents > 0 || row.booked_cost_cents > 0) && (
                      <div className="text-[11px] text-slate-400 font-normal">
                        {I18n.t("projects.margins.committed_short")} {euros(row.committed_cost_cents)}
                        {" · "}
                        {I18n.t("projects.margins.booked_short")} {euros(row.booked_cost_cents)}
                      </div>
                    )}
                    {row.has_actual && hours(row.actual_hours) && (
                      <div className="text-[11px] text-slate-400 font-normal">{hours(row.actual_hours)}</div>
                    )}
                  </td>
                  <td className={`${TD} font-semibold ${row.has_actual ? costTone(row.execution_variance_cents) : ""}`}>
                    {row.has_actual ? signedEuros(row.execution_variance_cents) : <Pending />}
                    {row.has_actual && row.budget_cost_cents > 0 && (
                      <div className="text-[11px] font-normal opacity-80">{pct(row.execution_variance_pct)}</div>
                    )}
                  </td>

                  {/* la marge */}
                  <td className={`${TD} text-slate-600 border-l border-slate-100`}>{coef(row.overhead_coef)}</td>
                  <td className={`${TD} text-slate-600`}>
                    {euros(row.budget_profit_cents)}
                    {row.quote_sale_cents > 0 && (
                      <div className="text-[11px] text-slate-400">{pct(row.budget_profit_pct)}</div>
                    )}
                  </td>
                  <td className={`${TD} font-semibold ${row.has_actual ? profitTone(row.actual_profit_cents) : ""}`}>
                    {row.has_actual ? euros(row.actual_profit_cents) : <Pending />}
                    {row.has_actual && row.quote_sale_cents > 0 && (
                      <div className="text-[11px] font-normal opacity-80">{pct(row.actual_profit_pct)}</div>
                    )}
                  </td>
                  <td className={`${TD} font-semibold ${row.has_actual ? profitTone(row.profit_variance_cents) : ""}`}>
                    {row.has_actual ? signedEuros(row.profit_variance_cents) : <Pending />}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot className="text-[13px]">
              <tr className="border-t-2 border-slate-300 bg-slate-50/70 font-semibold">
                <td className="px-3 py-3 text-[11px] uppercase tracking-wider text-slate-700">
                  {I18n.t("projects.margins.col.total")}
                </td>
                <td className={`${TD} text-slate-700`}>{euros(totals.quote_cost_cents)}</td>
                <td className={`${TD} text-slate-900`}>{euros(totals.quote_sale_cents)}</td>
                <td className={`${TD} text-slate-900 border-l border-slate-200`}>{euros(totals.budget_cost_cents)}</td>
                <td className={`${TD} ${costTone(totals.study_variance_cents)}`}>
                  {signedEuros(totals.study_variance_cents)}
                </td>
                <td className={`${TD} text-slate-900 border-l border-slate-200`}>
                  {totals.has_actual ? euros(totals.actual_cost_cents) : <Pending />}
                </td>
                <td className={`${TD} ${totals.has_actual ? costTone(totals.execution_variance_cents) : ""}`}>
                  {totals.has_actual ? signedEuros(totals.execution_variance_cents) : <Pending />}
                </td>
                <td className={`${TD} text-slate-700 border-l border-slate-200`}>{coef(totals.overhead_coef)}</td>
                <td className={`${TD} text-slate-700`}>
                  {euros(totals.budget_profit_cents)}
                  <div className="text-[11px] font-normal text-slate-400">{pct(totals.budget_profit_pct)}</div>
                </td>
                <td className={`${TD} ${totals.has_actual ? profitTone(totals.actual_profit_cents) : ""}`}>
                  {totals.has_actual ? euros(totals.actual_profit_cents) : <Pending />}
                  {totals.has_actual && (
                    <div className="text-[11px] font-normal opacity-80">{pct(totals.actual_profit_pct)}</div>
                  )}
                </td>
                <td className={`${TD} ${totals.has_actual ? profitTone(totals.profit_variance_cents) : ""}`}>
                  {totals.has_actual ? signedEuros(totals.profit_variance_cents) : <Pending />}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="mt-2 text-[12px] text-slate-400 max-w-[100ch]">
        {I18n.t("projects.margins.footnote")}
      </p>
    </section>
  )
}

export default MarginsSection
